/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of, throwError } from "rxjs";
import { first, map } from "rxjs/operators";
import { AbstractObjectList, uuid } from "@src/app/model/abstract-object";
import { User } from "@src/app/model/user";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase } from "@src/app/modules/importer/General/ParseOptionBase";
import { POBValidators } from "@src/app/modules/importer/General/POBValidators";
import { ImportRow } from "@src/app/modules/importer/ImportRow";
import { RoleService } from "@src/app/pages/role-page/role.service";
import { BCodeService } from "@src/app/modules/budget/bcode.service";
import { BCode } from "@src/app/model/bcode";
import { ParseOptionBaseBCode } from "@src/app/modules/importer/General/ParseOptionBaseBCode";
import { HttpClient } from "@angular/common/http";
import { TenantService } from '@src/app/shared/tenant.service';
import { MatDialog } from "@angular/material/dialog";
import { JournalPageComponent } from "@src/app/modules/txn/journal-page/journal-page.component";
import { TrialBalanceApiResponse } from "@src/app/modules/txn/balance/trial-balance/trial-balance.component";
import { Txn } from "@src/app/model/txn";
import { CycleService } from "@src/app/modules/budget/cycle.service";
import { Period } from "@src/app/model/period";
import { Cycle } from "@src/app/model/cycle";
import { BankAccountService } from "@src/app/modules/txn/bank-account.service";
import { BankAccount } from "@src/app/model/bankAccount";
import { NrHelper } from "@src/app/shared/NrHelper";
import { MyInjector } from "@src/app/app.module";
import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { ImportField } from "@src/app/modules/importer/ImportField";

interface ImportData { bCodeId: uuid, debit: number, credit: number }
@Injectable({
    providedIn: 'root'
})
export class TrialBalanceParserService implements ImportParser {

    gps = new GeneralParser();
    bCodeSvc = inject(BCodeService);
    currentUserSvc = inject(CurrentUserService);
    roleSvc = inject(RoleService);
    bankAccountSvc = inject(BankAccountService);
    http = inject(HttpClient);
    dialog = inject(MatDialog);
    cycleSvc = inject(CycleService);

    yearEndBalancePath = TenantService.ajaxPath + 'txn/firstYearEndBalances';
    yearEndTrialBalance: TrialBalanceApiResponse;
    txnPeriod: Period;

    bCodes : BCode[] = [];
    bankAccounts: BankAccount[] = [];
    importRows: ImportData[] = [];

    importDoc: ImportDoc;
    currentUser: User;

    bCodePOB = new ParseOptionBaseBCode('Budget Code', 'bCodeId', [POBValidators.requiredPOB()]);
    debitPOB = new ParseOptionBase('Debit', 'debit').asNumeric();
    creditPOB = new ParseOptionBase('Credit', 'credit').asNumeric();

    expectedDebtors = 0;
    expectedCreditors = 0;

    availableImportItems = [
        this.bCodePOB,
        this.debitPOB,
        this.creditPOB,
    ];

    deleteRow(row:ImportRow) {
        this.importDoc.removeRow(row);
        this.importDoc.finalise();
        console.warn('Row removed from import', row);
    }

    bCodeSorter(a, b) {
        if (((+a.typeId * 1000000) + a.sort) >= ((+b.typeId * 1000000) + b.sort)) {
            return 1
        }
        return -1
    }

    setUp() {
        this.importDoc = new ImportDoc();

        return forkJoin({
            currentUser: this.currentUserSvc.getCurrentUser().pipe(first()),
            bCodes: this.bCodeSvc.get<BCode>(true),
            yeBals: this.http.get<TrialBalanceApiResponse>(this.yearEndBalancePath),
            bankAccounts: this.bankAccountSvc.get<BankAccount>(true),
            dbcs: this.currentUserSvc.getDefaultBCodes().pipe(first())
        }).pipe(map(result => {
            console.log(result);
            this.currentUser = result.currentUser;
            this.bCodes = result.bCodes.sort(this.bCodeSorter);
            this.bankAccounts = result.bankAccounts;
            this.bCodePOB.setOptions(result.bCodes, this.importDoc);
            this.yearEndTrialBalance = result.yeBals;
            this.importDoc.addNote('Importing trial balance as at end of ' + this.yearEndTrialBalance.cycle.name);
            this.cycleSvc.getOne<Cycle>(this.yearEndTrialBalance.cycle.id, null).subscribe( cycle => {
                if (cycle === null) {
                    this.importDoc.addNote('No cycle found ', null, true);
                } else {
                    this.txnPeriod = cycle.periods.find(p => p.to === this.yearEndTrialBalance.cycle.to);
                    if (this.txnPeriod) {
                        this.importDoc.addNote('Journal will be in period ' + this.txnPeriod.name + ' dated ' + this.txnPeriod.to);
                    } else {
                        this.importDoc.addNote('No period found ', null, true);
                    }
                    if (this.txnPeriod.statusId !== Period.STATUS.OPEN.id) {
                        this.importDoc.addNote('Period is not open will not be able to post', null, true);
                    }
                }
            });

            return true;
        }));
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    getExpectedBalance(bCodeId: uuid) {
        const bal = this.yearEndTrialBalance.balances.find( b => b.bCodeId === bCodeId);
        if (bal) {
            return Math.round((+bal.debits - +bal.credits) * 100) / 100;
        } else {
            console.warn('No year end balance found for budget code', bCodeId);
            return 0;
        }
    }

    validateData() {

        let debits = 0;
        let credits = 0;

        let foundDebtors = false;
        const debtorsId = this.currentUserSvc.getDefaultBCode('Debtors').bCodeId;
        const expectedDebtors = this.getExpectedBalance(debtorsId);

        let foundCreditors = false;
        const creditorsId = this.currentUserSvc.getDefaultBCode('Creditors').bCodeId;
        const expectedCreditors = this.getExpectedBalance(creditorsId)

        const rows = this.importDoc.getGeneralRows();

        rows.forEach( row => {
            const item = row.importItem as ImportData;
            console.log({item, debtorsId, creditorsId, isDebtor: item.bCodeId === debtorsId})
            if (!item.bCodeId) {
                row.addError('No Budget Code');
            } else if (item.bCodeId === debtorsId) {
                foundDebtors = true;
                if (!NrHelper.equal(item.debit - item.credit, expectedDebtors)) {
                    console.log({error: 'Debtors not matched',
                        db: item.debit, cr: item.credit, bal: item.debit - item.credit,
                        expectedDebtors, diff: NrHelper.equal(item.debit - item.credit, expectedDebtors)});
                    row.addError('Debtors expected to be ' + expectedDebtors);
                }
            } else if (item.bCodeId === creditorsId) {
                foundCreditors = true;
                if (!NrHelper.equal(item.debit - item.credit, expectedCreditors)) {
                    console.log({error: 'Creditors not matched',
                        db: item.debit, cr: item.credit, bal: item.debit - item.credit,
                        expectedDebtors, diff: NrHelper.equal(item.debit - item.credit, expectedCreditors)});
                    row.addError('Creditors expected to be ' + expectedCreditors);
                }
            }
            if (item.debit) {
                debits += item.debit;
            }
            if (item.credit) {
                credits += item.credit;
            }
            if (!item.debit && !item.credit) {
                row.addError('No Debit or Credit amount entered');
            }
        })
        const diff = NrHelper.toCurrency(debits - credits);

        if (Math.abs(diff) >= 0.01) {
            this.importDoc.addNote(`Debit ${debits} and Credit ${credits} do not balance (Diff ${diff})`, null, true);
        }
        if (!foundDebtors) {
            if (!NrHelper.equal(expectedDebtors, 0.00)) {
                this.importDoc.addNote(`No debtors balance found`, null, true);
            }
        }

        if (!foundCreditors) {
            if (!NrHelper.equal(expectedCreditors, 0.00)) {
                this.importDoc.addNote(`No creditors balance found`, null, true);
            }

        }

        this.importDoc.finalise();
        if (this.importDoc.needsFix()) {
            return false;
        } else if (this.importDoc.hasErrors()) {
            return false;
        }
        return true;
    }

    createJournalHeader() {
        const txn = new Txn();
        txn.ledgerId = Txn.LEDGER.GL.id;
        txn.typeId = Txn.TYPE.JRNL.id;
        txn.txnDate = this.txnPeriod.to;
        txn.txnCycleId = this.txnPeriod.cycleId
        txn.txnPeriodId = this.txnPeriod.id;
        txn.reference = 'Import Initial Trial Balance';
        txn.notes = 'Zeroing trial balance and importing new values';
        txn.details = [];
        return txn;
    }

    createChildItem(parent: Txn, bCodeId: uuid): Txn {
        const txn = new Txn();
        txn.ledgerId = Txn.LEDGER.GL.id;
        txn.typeId = Txn.TYPE.JRNL_DETAIL.id;
        txn.txnDate = parent.txnDate;
        txn.txnPeriodId = parent.txnPeriodId;
        txn.txnCycleId = parent.txnCycleId;
        txn.bCodeId = bCodeId;
        const bankAccount = this.bankAccounts.find( ba => ba.bCodeId === bCodeId);
        if (bankAccount) {
            txn.bankAccountId = bankAccount.id;
        }
        parent.details.push(txn);
        return txn;
    }

    createJournal() {
        const jrnl = this.createJournalHeader();
        this.bCodes.forEach( b => {
            const prevBalance = this.getPrevBalance(b);
            const newBalance = this.getNewBalance(b);
            if (!NrHelper.equal(prevBalance, newBalance)) {
                this.zeroPrevBalance(jrnl, b, prevBalance);
                this.setNewBalance(jrnl, b, newBalance);
            }
        });
        this.setJournalTotals(jrnl);
        return jrnl;
    }

    setJournalTotals(jrnl: Txn) {
        jrnl['totalDebit'] = 0;
        jrnl['totalCredit'] = 0;
        jrnl.details.forEach(item => {
            jrnl['totalDebit'] += item.debit;
            jrnl['totalCredit'] += item.credit;
        });
        jrnl['totalDebit'] = NrHelper.toCurrency(jrnl['totalDebit']);
        jrnl['totalCredit'] = NrHelper.toCurrency(jrnl['totalCredit']);
    }

    getPrevBalance(bCode: BCode) {
        const prevBal = this.yearEndTrialBalance.balances.find(o => o.bCodeId === bCode.id);
        if (prevBal) {
            return NrHelper.toCurrency(+prevBal.debits - +prevBal.credits);
        } else {
            return 0.00;
        }
    }

    getNewBalance(bCode: BCode) {
        const newBal = this.importRows.find( r => r.bCodeId === bCode.id);
        if (newBal) {
            return NrHelper.toCurrency(newBal.debit - newBal.credit);
        } else {
            return 0.00;
        }
    }

    zeroPrevBalance(jrnl: Txn, bCode: BCode, amt: number) {

        if (Math.abs(amt) > 0.01) {
            const item = this.createChildItem(jrnl, bCode.id);
            if (amt > 0) {
                item.credit = amt;
            } else {
                item.debit = -amt;
            }
            item.notes = 'Zero Previous Balance';
        }
    }

    setNewBalance(jrnl: Txn, bCode: BCode, amt: number) {
        if (Math.abs(amt) > 0.01) {
            const item = this.createChildItem(jrnl, bCode.id);
            if (amt < 0) {
                item.credit = -amt;
            } else {
                item.debit = amt;
            }
            item.notes = 'Imported Balance';
        }
    }

    postToServer(): Observable<AbstractObjectList> {
        this.importRows = this.gps.getItemsToPost() as unknown as ImportData[];
        console.log(this.importRows);
        if (!this.validateData()) {
            return throwError(new Error('Validation Errors'));
        }
        const journalToPost = this.createJournal();

        if(journalToPost.details.length === 0) {
            const cds = MyInjector.instance.get(ConfirmDialogService);
            const title = $localize`Great Job`;
            const message = $localize`You are importing ${this.importRows.length} items - And for every single one of them the balance you are imporing exactly matches the balance in the system. So there is no amending journals required. Every piece of history has been precisely and accurately imported. Well done!`;
            cds.alert(title, message);
            this.importDoc.getGeneralRows().forEach( row => {
                row.add(new ImportField('Balanced', 'Balanced').setValue('Balance Matches'))
            });
            return of(null)
        }

        return this.dialog.open(JournalPageComponent, {
            data: {hideTabs: true, journalToPost}}
        ).afterClosed();
    }
}
